import { CalendarClock, ShieldCheck } from 'lucide-react';
import { DELAI_AMO_MOIS, statutAmo } from '@sirajoptic/shared';
import { aujourdhui, date } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * Où en est ce client de son délai de renouvellement AMO.
 *
 * L'écran annonce une **date à partir de laquelle** un remboursement redevient
 * possible — jamais un remboursement acquis. La nuance n'est pas de style :
 * promettre au comptoir une prise en charge qui n'arrive pas se paie, et le
 * magasin ne connaît ni le régime du client ni ses droits ouverts.
 *
 * Rien ne s'affiche sans date de départ : une échéance inventée serait pire
 * que pas d'échéance.
 */
export function BandeauAmo({
  depart,
  estime,
  className,
}: {
  depart: string | null;
  /** Vrai quand `depart` est une date de commande, faute d'ordonnance datée. */
  estime: boolean;
  className?: string;
}) {
  const statut = statutAmo(depart, aujourdhui());
  if (!statut) return null;

  const renouvelable = statut.etat === 'RENOUVELABLE';
  const Icone = renouvelable ? ShieldCheck : CalendarClock;

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-2 rounded-card border px-4 py-3 text-sm',
        renouvelable
          ? 'border-success/30 bg-success-soft text-ink'
          : 'border-line bg-surface-sunken text-ink-soft',
        className,
      )}
    >
      <Icone
        aria-hidden
        className={cn('h-4 w-4 shrink-0', renouvelable ? 'text-success' : 'text-ink-muted')}
      />

      <span className="min-w-0">
        {renouvelable ? (
          <>
            <strong className="font-medium">Renouvellement AMO possible</strong> depuis le{' '}
            {date(statut.echeance)}.
          </>
        ) : (
          <>
            <strong className="font-medium">Renouvellement AMO</strong> à partir du{' '}
            {date(statut.echeance)} — {delaiEnClair(statut.jours)}.
          </>
        )}
      </span>

      <span className="ml-auto shrink-0 text-xs text-ink-muted">
        {DELAI_AMO_MOIS} mois depuis le {date(statut.depart)}
        {estime ? ', date de commande' : ", date d'ordonnance"}
      </span>
    </div>
  );
}

/**
 * « dans 12 jours », « dans 8 mois ».
 *
 * Au-delà de deux mois, un décompte en jours ne se lit plus : « dans 512
 * jours » demande un calcul mental que personne ne fait au comptoir.
 */
function delaiEnClair(jours: number): string {
  if (jours <= 1) return "demain au plus tard";
  if (jours < 60) return `dans ${jours} jours`;

  // 30,44 : la longueur moyenne d'un mois, pour ne pas dériver sur deux ans.
  const mois = Math.round(jours / 30.44);
  return `dans ${mois} mois`;
}
